import React, { useState, useEffect, useRef } from "react";
import TextEditor from "../../../../Main/TextEditor";
import {
  BorderedNumberInput,
  BorderedTextInput,
} from "../../../../HouseStyle/Components/Inputs";
import { ButtonDarkBlue } from "../../../../HouseStyle/Components/Buttons";
import middleware from "../../../../Api/Middleware";
import ToastSuccess from "../../../../Toasts/ToastSuccess";
import ToastError from "../../../../Toasts/ToastError";
import { BorderedDropDown, RegularDropDown } from "../../../../HouseStyle/Components/DropDowns";
import Dropdownfilter from "../../../../Filters/FilterComponents/Dropdownfilter";

const FirstBox = (props) => {
  const maxBarcodeLength=25;
  const [salesChannels, setSalesChannels] = useState([]);
  const [channelDescriptions, setChannelDescriptions] = useState({});
  const [selectedDescriptionChannel, setSelectedDescriptionChannel] = useState('default');
  const [currentDescription, setCurrentDescription] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [seoAppInstalled, setSeoAppInstalled] = useState(() => {
    const appstore = JSON.parse(localStorage.getItem("appstore"));
    return appstore?.apps?.find(app => app.slug === "seo-duplicates")?.status === 3;
  });
  
  const saveTimeoutRef = useRef(null);
  const editorKey = useRef(0);

  // Fetch sales channels on component mount
  useEffect(() => {
    if (props.edit && props.product?.product_id) {
      fetchSalesChannels();
      fetchChannelDescriptions();
    }
  }, [props.product?.product_id]);

  // Update current description when selected channel changes
  useEffect(() => {
    let newDescription = '';
    if (selectedDescriptionChannel === 'default') {
      newDescription = props.product?.product_description || '';
    } else {
      // If channel has a specific description, use it; otherwise use the default product description
      const channelDesc = channelDescriptions[selectedDescriptionChannel];
      newDescription = channelDesc || props.product?.product_description || '';
    }
    
    if (newDescription !== currentDescription) {
      setCurrentDescription(newDescription);
      editorKey.current += 1; // Force TextEditor re-render
      setHasChanges(false);
    }
  }, [selectedDescriptionChannel, channelDescriptions, props.product?.product_description]);

  const fetchSalesChannels = async () => {
    try {
      const response = await middleware.get('sp/salesChannelsTableAll?page=1&page_size=1000&filtering=status=Actief');
      setSalesChannels(response.data.data || []);
    } catch (error) {
      console.error('Error fetching sales channels:', error);
    }
  };

  const fetchChannelDescriptions = async () => {
    if (!props.product?.product_id) return;
    
    try {
      const response = await middleware.get(`products/${props.product.product_id}/sales-channel-descriptions`);
      setChannelDescriptions(response.data.data || {});
    } catch (error) {
      console.error('Error fetching channel descriptions:', error);
    }
  };

  const saveChannelDescription = async (channelId, description) => {
    if (!props.product?.product_id) return;
    
    try {
      await middleware.put(`products/${props.product.product_id}/sales-channel-descriptions`, {
        sales_channel_id: channelId,
        description: description
      });
      
      // If description is empty, remove it from state completely
      if (description === '' || description === null) {
        setChannelDescriptions(prev => {
          const newDescriptions = { ...prev };
          delete newDescriptions[channelId];
          return newDescriptions;
        });
      } else {
        setChannelDescriptions(prev => ({
          ...prev,
          [channelId]: description
        }));
      }
      
      ToastSuccess('Verkoopkanaal omschrijving opgeslagen', 'channel_description_saved', 2000);
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving channel description:', error);
      ToastError('Fout bij het opslaan van de omschrijving');
    }
  };

  const handleDescriptionChange = (value) => {
    setCurrentDescription(value);
    setHasChanges(true);
    
    // Update the appropriate description
    if (selectedDescriptionChannel === 'default') {
      props.setProduct({ ...props.product, product_description: value });
      props.edit && props.setUpdatedProduct(true);
    } else {
      // Clear existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      // Set new timeout for auto-save (3 seconds after last change)
      saveTimeoutRef.current = setTimeout(() => {
        saveChannelDescription(selectedDescriptionChannel, value);
      }, 3000);
    }
  };

  const handleChannelSwitch = (newChannelId) => {
    // Save current changes before switching if there are any
    if (hasChanges && selectedDescriptionChannel !== 'default') {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveChannelDescription(selectedDescriptionChannel, currentDescription);
    }
    
    setSelectedDescriptionChannel(newChannelId);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const formatProductNumberPlaceholder = () => {
    if (!props?.product?.product_name) return "";
    return props.product.product_name
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-');
  }

  return (
    <>
      <div className="whiteBox p-5 mt-0 align-center">
        <div className="d-flex flex-column mb-3">
          <label className="inputTitle standardLabel fw-medium">
            Productnaam
          </label>
          <BorderedTextInput
            size="l"
            inputValue={props?.product?.product_name ?? ""}
            setInputValue={(newValue) => {
              props.setProduct({
                ...props.product,
                product_name: newValue,
              });
            }}
            updateFunction={props.edit ? props.handleChange : undefined}
            updateWaitTimer={null}
            disallowSemicolon={true}
          />
        </div>
        <div className="d-flex flex-row flex-wrap productNumSelectLists col-12 justify-content-between mb-3" style={{ gap: '1rem' }}>
          <div
            className="d-flex flex-column"
            style={
              props.product.type != 2 ? { width: "32.5%" } : { width: "48%" }
            }
          >
            <div className="d-flex flex-nowrap w-100 mb-2">
              <label className="inputTitle standardLabel fw-medium" style={{ fontSize: '14px' }}>
                Productnummer
              </label>
            </div>
            <BorderedTextInput
              size="l"
              inputValue={props?.product?.product_number ?? ""}
              setInputValue={(newProductNumber) => {
                props.setProduct({
                  ...props.product,
                  product_number: newProductNumber,
                });
              }}
              placeholder={formatProductNumberPlaceholder()}
              updateFunction={
                props.edit
                  ? () => {
                      props.handleChange();
                    }
                  : undefined
              }
              updateWaitTimer={null}
            />
          </div>
          {props.product.type != 2 ? (
            <div className="d-flex flex-column" style={{ width: "32.5%", minWidth: '0', overflow: 'visible' }}>
              <div className="d-flex flex-row justify-content-between align-items-center mb-2 gap-2" style={{ flexWrap: 'wrap' }}>
                <label className="inputTitle standardLabel fw-medium" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                  Barcode
                </label>

                <select
                  className="smallStandardInput standardDropdownArrow fitContent"
                  id="sel1"
                  name="sellist1"
                  value={props?.selectedOption ?? ""}
                  onChange={(e) => {
                    props.setSelectedOption(e.target.value);
                  }}
                  style={{ fontSize: '0.75rem', maxWidth: '100%', width: 'auto', paddingRight: '30px', paddingLeft: '10px', paddingTop: '5px', paddingBottom: '5px' }}
                >
                  <option value="3" hidden>
                    -- Selecteer een optie --
                  </option>
                  <option value="0">Aangepaste barcode</option>
                  <option value="2">Automatisch gegenereerd</option>
                </select>
              </div>

              <div className="d-flex flex-column marginRight0">
                {props.selectedOption === "0" && (
                  <BorderedTextInput
                    size="l"
                    placeholder="Barcode (ISBN, UPS, GTIN, etc.)"
                    inputValue={props.barcode === null ? "" : props.barcode}
                    setInputValue={(Value) => {
                      if(Value.length<=maxBarcodeLength&&!Value.includes(' ')) props.setBarcode(Value);
                    }}
                    updateFunction={props.edit ? props.handleChange : undefined}
                    updateWaitTimer={null}
                  />
                )}
                {props.selectedOption === "2" && (
                  <BorderedTextInput
                    size="l"
                    placeholder="Barcode (ISBN, UPS, GTIN, etc.)"
                    inputValue={props.barcode === null ? "" : props.barcode}
                    updateFunction={props.edit ? props.handleChange : undefined}
                    updateWaitTimer={null}
                  />
                )}
                {props.selectedOption === "3" && (
                  <BorderedTextInput
                    size="l"
                    placeholder="Barcode (ISBN, UPS, GTIN, etc.)"
                    inputValue={props.barcode === null ? "" : props.barcode}
                    setInputValue={(Value) => {
                      if (Value.length <= maxBarcodeLength&&!Value.includes(' ')) props.setBarcode(Value);
                    }}
                    updateFunction={props.edit ? props.handleChange : undefined}
                    updateWaitTimer={null}
                  />
                )}
              </div>
            </div>
          ) : (
            <></>
          )}
          <div
            className="d-flex flex-column"
            style={
              props.product.type != 2 ? { width: "32.5%" } : { width: "48%" }
            }
          >
            <div className="d-flex flex-column mb-2">
              <label className="inputTitle standardLabel fw-medium" style={{ fontSize: '14px' }}>
                Merk
              </label>
            </div>
            <div className="d-flex flex-column">
              {props.edit ? (
                <BorderedDropDown
                  placeholder="Selecteer een merk"
                  searchable={true}
                  options={[
                    { title: "Selecteer een merk", value: "" },
                    ...props.brands.map((e) => ({
                      value: e.brand_id,
                      title: e.name,
                    })),
                  ]}
                  selectedValue={props.brand?.brand_id || ""}
                  setSelectedValue={(value) => {
                    props.setProductBrand({
                      ...props.brand,
                      brand_id: value,
                    });
                    props.edit && props.setUpdatedProduct(true);
                  }}
                />
              ) : (
                <BorderedDropDown
                  placeholder="Selecteer een merk"
                  searchable={true}
                  options={[
                    { title: "Selecteer een merk", value: "" },
                    ...props.brands.map((e) => ({
                      value: e.brand_id,
                      title: e.name,
                    })),
                  ]}
                  selectedValue={props.brandId ? props.brandId : 'NOT'}
                  setSelectedValue={(value) => props.setBrandId(value)}
                />
              )}
            </div>
          </div>
        </div>

        {props.product.type != 2 && (
          <div className="d-flex flex-row flex-wrap productNumSelectLists col-12 justify-content-between mb-3" style={{ gap: '1rem' }}>
            <div
              className="d-flex flex-column"
              style={
                props.product.type != 2 ? { width: "32.5%" } : { width: "48%" }
              }
            >
              <div className="d-flex flex-nowrap w-100 mb-2">
                <label className="inputTitle standardLabel fw-medium" style={{ fontSize: '14px' }}>
                Kleur
                </label>
              </div>
              <BorderedDropDown
                placeholder="Selecteer een kleur"
                searchable={true}
                options={[
                  { title: "Selecteer een kleur", value: -1 },
                  ...props.colors.map((e) => ({
                    value: e.color_id,
                    title: e.name,
                  })),
                ]}
                selectedValue={props.color}
                setSelectedValue={(e)=>{
                  props.setColor(e);
                  if (props.setUpdateMetaData) props.setUpdateMetaData(true);
                }}
              />
            </div>

            <div
              className="d-flex flex-column"
              style={
                props.product.type != 2 ? { width: "32.5%" } : { width: "48%" }
              }
            >
              <div className="d-flex flex-row justify-content-between align-items-center mb-2 gap-2" style={{ flexWrap: 'wrap' }}>
                <label className="inputTitle standardLabel fw-medium" style={{ fontSize: '14px' }}>
                Maat
              </label>
              </div>

              <div className="d-flex flex-column marginRight0">
                <BorderedDropDown
                  placeholder="Selecteer een maat"
                  searchable={true}
                  options={[
                    { title: "Selecteer een maat", value: -1 },
                    ...props.sizes.map((e) => ({
                      value: e.size_id,
                      title: e.main,
                    })),
                  ]}
                  selectedValue={props.size}
                  setSelectedValue={e => {
                    props.setSize(e);
                    if (props.setUpdateMetaData) props.setUpdateMetaData(true);
                  }}
                />
              </div>
            </div>
            
            <div
              className="d-flex flex-column"
              style={
                props.product.type != 2 ? { width: "32.5%" } : { display: "none" }
              }
            >
              {/* This empty div is to keep the spacing consistent */}
            </div>
          </div>
        )}

        <div>
          <div className="mb-2">
            <div className="d-flex flex-row justify-content-between align-items-center mb-2">
              <label className="inputTitle ms-3 fw-medium" style={{ fontSize: '14px' }}>
                Productomschrijving
              </label>
              {props.edit && seoAppInstalled && salesChannels.length > 0 && (
                <div className="d-flex align-items-baseline gap-2" style={{paddingBottom:0}}>
                  <label className="inputTitle fw-medium mb-3" style={{ fontSize: '14px', marginTop: '2px', marginBottom: '0px' }}>
                    Verkoopkanaal:
                  </label>
                  <select
                    className="standardInput standardDropdownArrow"
                    style={{ width: 'auto', minWidth: '200px', fontSize: '14px', margin: 0, paddingTop: '6px', paddingBottom: '6px', height: '36px' }}
                    value={selectedDescriptionChannel}
                    onChange={(e) => handleChannelSwitch(e.target.value)}
                  >
                    <option value="default">Standaard</option>
                    {salesChannels.map((channel) => (
                      <option key={channel.sales_channel_id} value={channel.sales_channel_id}>
                        {channel.sales_channel_name}
                        {channelDescriptions[channel.sales_channel_id] ? ' ✓' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            {props.edit && seoAppInstalled && selectedDescriptionChannel !== 'default' && (
              <small className="text-muted ms-3 d-block mb-2" style={{ fontSize: '12px' }}>
                {channelDescriptions[selectedDescriptionChannel] 
                  ? 'Deze omschrijving wordt gebruikt voor dit verkoopkanaal' 
                  : 'Nog geen specifieke omschrijving - standaard omschrijving wordt gebruikt'}
                {hasChanges && (
                  <span className="ms-2 text-warning">
                    • Wijzigingen worden automatisch opgeslagen...
                  </span>
                )}
                {channelDescriptions[selectedDescriptionChannel] && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary ms-3"
                    onClick={(e) => {
                      e.preventDefault();
                      saveChannelDescription(selectedDescriptionChannel, '');
                      ToastSuccess('Kanaal teruggezet naar standaard omschrijving', 'kanaal_reset', 2000);
                    }}
                    style={{ fontSize: '10px', padding: '2px 6px' }}
                  >
                    Reset naar standaard
                  </button>
                )}
              </small>
            )}
            <TextEditor
              key={`editor-${selectedDescriptionChannel}-${editorKey.current}`}
              onBlur={(e) => {
                handleDescriptionChange(e);
              }}
              value={currentDescription}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default FirstBox;